import { useEffect, useState } from 'react'
import {
  useBillingSettings,
  useCancelSubscriptionInvoice,
  useCustomers,
  useDeleteSubscription,
  useExportStatus,
  useRunBilling,
  useSaveBillingSettings,
  useSaveSubscription,
  useSubscriptionInvoices,
  useSubscriptions
} from '../../api/queries'
import { subscriptionIntervals } from '../../api/types'
import { Card, EmptyState, ErrorBox, Loading } from '../../components/ui'
import { formatDate } from '../../lib/format'
import type { BillingSettings, Subscription } from '../../api/types'

/** Beløb i danske kroner. Abonnementsbeløb er altid uden moms. */
const kr = (amount: number) =>
  amount.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const intervalLabel = (months: number) =>
  subscriptionIntervals.find((i) => i.months === months)?.label ?? `Hver ${months}. måned`

const statusLabels: Record<string, string> = {
  pending: 'Afventer',
  exported: 'Sendt',
  failed: 'Fejlede',
  cancelled: 'Annulleret'
}

/**
 * Abonnementer og fakturering (beslutning 031).
 *
 * Siden viser aftalerne og de faktureringsgrundlag, de har dannet. Selve
 * fakturaen laves i økonomisystemet - her afgøres kun hvem, hvad, hvor ofte
 * og hvor meget.
 */
export function SubscriptionsPage() {
  const [includeEnded, setIncludeEnded] = useState(false)
  const [editing, setEditing] = useState<Subscription | 'new' | null>(null)

  const subscriptions = useSubscriptions(null, includeEnded)
  const invoices = useSubscriptionInvoices()
  const exportStatus = useExportStatus()
  const runBilling = useRunBilling()

  if (subscriptions.isLoading) return <Loading />
  if (subscriptions.error) return <ErrorBox error={subscriptions.error} />

  const rows = subscriptions.data ?? []
  const pending = (invoices.data ?? []).filter((i) => i.status === 'pending')

  return (
    <div className="stack" style={{ gap: 16 }}>
      {exportStatus.data && !exportStatus.data.isConfigured && (
        <Card>
          <strong>Fakturaerne sendes ikke automatisk endnu.</strong>
          <div className="small muted" style={{ marginTop: 6 }}>
            Systemet holder styr på abonnementerne og danner grundlaget for hver regning, men
            afleveringen til {exportStatus.data.systemName} er ikke sat op. Indtil den er, skal
            fakturaerne oprettes i hånden ud fra listen nedenfor. Se docs/TODO.md for, hvad der
            mangler.
          </div>
        </Card>
      )}

      {/* Formularen ligger øverst, ikke nederst: knappen sidder i toppen, og et
          skema der åbner under tre kort er der ingen der finder. */}
      {editing && (
        <SubscriptionForm
          subscription={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}

      <Card
        title="Abonnementer"
        actions={
          <button className="btn btn--sm btn--primary" onClick={() => setEditing('new')}>
            Nyt abonnement
          </button>
        }
      >
        <label className="row small" style={{ gap: 6, marginBottom: 12 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={includeEnded}
            onChange={(e) => setIncludeEnded(e.target.checked)}
          />
          <span>Vis også afsluttede aftaler</span>
        </label>

        {rows.length === 0 ? (
          <EmptyState>Der er ingen abonnementer endnu.</EmptyState>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Kunde</th>
                  <th>Aftale</th>
                  <th>Anlæg</th>
                  <th style={{ textAlign: 'right' }}>Beløb</th>
                  <th>Interval</th>
                  <th>Næste fakturering</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} style={{ opacity: s.isPaused ? 0.55 : 1 }}>
                    <td>{s.customerName}</td>
                    <td>
                      {s.description}
                      {s.isPaused && <span className="tiny muted"> · på pause</span>}
                    </td>
                    <td className="small muted">
                      {s.deviceName ? `${s.deviceName} (${s.deviceCode})` : 'Hele kunden'}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{kr(s.amount)} kr.</td>
                    <td className="small">{intervalLabel(s.intervalMonths)}</td>
                    <td className="small">{formatDate(s.nextInvoiceDate)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn btn--sm" onClick={() => setEditing(s)}>
                        Ret
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="tiny muted" style={{ marginTop: 10 }}>
          Alle beløb er uden moms. Momsen lægges på i økonomisystemet, så den kun beregnes ét sted.
        </div>
      </Card>

      <Card
        title="Klar til fakturering"
        actions={
          <button
            className="btn btn--sm"
            disabled={runBilling.isPending}
            onClick={() => runBilling.mutate()}
          >
            {runBilling.isPending ? 'Danner ...' : 'Dan grundlag nu'}
          </button>
        }
      >
        {runBilling.data && (
          <div className="small" style={{ marginBottom: 10 }}>
            {runBilling.data.created === 0 ? (
              <>Der var ingen aftaler, der forfaldt.</>
            ) : (
              <>
                Dannede {runBilling.data.created} grundlag på i alt {kr(runBilling.data.total)} kr.
                uden moms.
                {runBilling.data.skipped > 0 && (
                  <> {runBilling.data.skipped} blev sprunget over (på pause eller udløbet).</>
                )}
              </>
            )}
          </div>
        )}
        {runBilling.error && <ErrorBox error={runBilling.error} />}

        {invoices.isLoading ? (
          <Loading />
        ) : pending.length === 0 ? (
          <EmptyState>Der er intet, der venter på at blive faktureret.</EmptyState>
        ) : (
          <InvoiceTable invoices={pending} />
        )}
      </Card>

      <BillingSettingsCard exportIsConfigured={exportStatus.data?.isConfigured ?? false} />

      <Card title="Sådan virker abonnementerne">
        <ul className="small muted" style={{ margin: 0, paddingLeft: 18 }}>
          <li>Der faktureres forud: en årlig aftale fra 1. januar dækker hele året frem.</li>
          <li>Aftaler på pause og udløbne aftaler danner ikke grundlag.</li>
          <li>Den samme periode kan ikke faktureres to gange, uanset hvor tit kørslen gentages.</li>
          <li>Beløbet kopieres ind, når grundlaget dannes - en senere prisændring rører det ikke.</li>
          <li>Automatisk fakturering er slået fra som standard og skal tændes bevidst.</li>
        </ul>
      </Card>
    </div>
  )
}

/**
 * Til/fra for den automatiske fakturering.
 *
 * Kontakten hører hjemme i brugerfladen og ikke kun i databasen: et ubemandet
 * job, der laver regninger, skal kunne slukkes af den, der opdager problemet -
 * uden hjælp fra en udvikler.
 */
function BillingSettingsCard({ exportIsConfigured }: { exportIsConfigured: boolean }) {
  const settings = useBillingSettings()
  const save = useSaveBillingSettings()
  const [form, setForm] = useState<BillingSettings | null>(null)

  useEffect(() => {
    if (settings.data) setForm(settings.data)
  }, [settings.data])

  if (settings.isLoading || !form) return null
  if (settings.error) return <ErrorBox error={settings.error} />

  return (
    <Card title="Automatisk fakturering">
      <p className="small muted" style={{ marginTop: 0 }}>
        Når den er tændt, danner systemet selv dagens faktureringsgrundlag én gang i døgnet.
        Er den slukket, sker det kun, når du trykker "Dan grundlag nu".
      </p>

      <div className="stack" style={{ gap: 12 }}>
        <label className="row" style={{ gap: 8 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          />
          <span>Dan faktureringsgrundlag automatisk</span>
        </label>

        <label className="stack" style={{ gap: 4 }}>
          <span className="small">Kør hver dag kl.</span>
          <select
            value={form.runHour}
            onChange={(e) => setForm({ ...form, runHour: Number(e.target.value) })}
            style={{ maxWidth: 120 }}
          >
            {Array.from({ length: 24 }, (_, hour) => (
              <option key={hour} value={hour}>
                {String(hour).padStart(2, '0')}:00
              </option>
            ))}
          </select>
          <span className="tiny muted">Dansk tid. Serveren kører UTC, men klokkeslættet her er dansk.</span>
        </label>
      </div>

      {form.enabled && !exportIsConfigured && (
        <div className="small" style={{ marginTop: 12, color: 'var(--danger)' }}>
          Bemærk: afleveringen til økonomisystemet virker ikke endnu, så grundlagene bliver
          liggende her, indtil nogen opretter fakturaerne i hånden.
        </div>
      )}

      <div className="row" style={{ gap: 8, marginTop: 14 }}>
        <button className="btn btn--primary" disabled={save.isPending} onClick={() => save.mutate(form)}>
          {save.isPending ? 'Gemmer ...' : 'Gem'}
        </button>
        {save.isSuccess && !save.isPending && (
          <span className="small" style={{ color: 'var(--success)', alignSelf: 'center' }}>
            Gemt. Virker med det samme.
          </span>
        )}
      </div>
      {save.error && <ErrorBox error={save.error} />}
    </Card>
  )
}

function InvoiceTable({ invoices }: { invoices: import('../../api/types').SubscriptionInvoice[] }) {
  const cancel = useCancelSubscriptionInvoice()

  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="table">
        <thead>
          <tr>
            <th>Kunde</th>
            <th>Tekst</th>
            <th>Periode</th>
            <th style={{ textAlign: 'right' }}>Beløb</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {invoices.map((i) => (
            <tr key={i.id}>
              <td>{i.customerName}</td>
              <td className="small">{i.description}</td>
              <td className="small muted" style={{ whiteSpace: 'nowrap' }}>
                {formatDate(i.periodStart)} - {formatDate(i.periodEnd)}
              </td>
              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{kr(i.amount)} kr.</td>
              <td className="small">
                {statusLabels[i.status] ?? i.status}
                {i.exportError && <div className="tiny" style={{ color: 'var(--danger)' }}>{i.exportError}</div>}
              </td>
              <td style={{ textAlign: 'right' }}>
                <button
                  className="btn btn--sm"
                  disabled={cancel.isPending}
                  onClick={() => {
                    if (confirm('Annullér dette faktureringsgrundlag? Der bliver ikke sendt en regning.')) {
                      cancel.mutate(i.id)
                    }
                  }}
                >
                  Annullér
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function SubscriptionForm({
  subscription,
  onClose
}: {
  subscription: Subscription | null
  onClose: () => void
}) {
  const customers = useCustomers()
  const save = useSaveSubscription()
  const remove = useDeleteSubscription()

  const today = new Date().toISOString().slice(0, 10)

  const [form, setForm] = useState({
    customerId: subscription?.customerId ? String(subscription.customerId) : '',
    deviceId: subscription?.deviceId ? String(subscription.deviceId) : '',
    description: subscription?.description ?? '',
    amount: subscription ? String(subscription.amount) : '',
    intervalMonths: subscription?.intervalMonths ?? 12,
    startDate: subscription?.startDate?.slice(0, 10) ?? today,
    nextInvoiceDate: subscription?.nextInvoiceDate?.slice(0, 10) ?? '',
    endDate: subscription?.endDate?.slice(0, 10) ?? '',
    isPaused: subscription?.isPaused ?? false,
    notes: subscription?.notes ?? ''
  })
  const [error, setError] = useState<string | null>(null)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)

    try {
      await save.mutateAsync({
        id: subscription?.id,
        body: {
          customerId: Number(form.customerId),
          deviceId: form.deviceId ? Number(form.deviceId) : null,
          description: form.description,
          amount: Number(form.amount.replace(',', '.')),
          intervalMonths: form.intervalMonths,
          startDate: form.startDate,
          nextInvoiceDate: form.nextInvoiceDate || null,
          endDate: form.endDate || null,
          isPaused: form.isPaused,
          notes: form.notes || null
        }
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke gemme abonnementet.')
    }
  }

  return (
    <Card title={subscription ? 'Ret abonnement' : 'Nyt abonnement'}>
      <form className="stack" style={{ gap: 12 }} onSubmit={submit}>
        <label className="stack" style={{ gap: 4 }}>
          <span className="small">Kunde</span>
          <select
            value={form.customerId}
            onChange={(e) => setForm({ ...form, customerId: e.target.value, deviceId: '' })}
            required
          >
            <option value="">Vælg kunde ...</option>
            {(customers.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <label className="stack" style={{ gap: 4 }}>
          <span className="small">Tekst på fakturalinjen</span>
          <input
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Serviceaftale, nødbelysning"
            maxLength={200}
            required
          />
        </label>

        <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
          <label className="stack" style={{ gap: 4, flex: 1 }}>
            <span className="small">Beløb pr. periode (uden moms)</span>
            <input
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              inputMode="decimal"
              placeholder="2400"
              required
            />
          </label>

          <label className="stack" style={{ gap: 4, flex: 1 }}>
            <span className="small">Faktureres</span>
            <select
              value={form.intervalMonths}
              onChange={(e) => setForm({ ...form, intervalMonths: Number(e.target.value) })}
            >
              {subscriptionIntervals.map((i) => (
                <option key={i.months} value={i.months}>
                  {i.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
          <label className="stack" style={{ gap: 4, flex: 1 }}>
            <span className="small">Aftalen gælder fra</span>
            <input
              type="date"
              value={form.startDate}
              onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              required
            />
          </label>

          <label className="stack" style={{ gap: 4, flex: 1 }}>
            <span className="small">Slutdato (valgfri)</span>
            <input
              type="date"
              value={form.endDate}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
            />
          </label>
        </div>

        {subscription && (
          <label className="stack" style={{ gap: 4 }}>
            <span className="small">Næste fakturering</span>
            <input
              type="date"
              value={form.nextInvoiceDate}
              onChange={(e) => setForm({ ...form, nextInvoiceDate: e.target.value })}
            />
            <span className="tiny muted">
              Rykkes normalt frem af sig selv. Ret den kun, hvis noget skal faktureres om.
            </span>
          </label>
        )}

        <label className="row" style={{ gap: 8 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={form.isPaused}
            onChange={(e) => setForm({ ...form, isPaused: e.target.checked })}
          />
          <span>Sat på pause - der faktureres ikke</span>
        </label>

        <label className="stack" style={{ gap: 4 }}>
          <span className="small">Note (valgfri)</span>
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </label>

        {error && <div className="small" style={{ color: 'var(--danger)' }}>{error}</div>}

        <div className="row" style={{ gap: 8 }}>
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Gemmer ...' : 'Gem'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Annullér
          </button>
          {subscription && (
            <button
              className="btn btn--danger"
              type="button"
              style={{ marginLeft: 'auto' }}
              onClick={async () => {
                if (!confirm(`Slet abonnementet "${subscription.description}"?`)) return
                try {
                  await remove.mutateAsync(subscription.id)
                  onClose()
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Kunne ikke slette abonnementet.')
                }
              }}
            >
              Slet
            </button>
          )}
        </div>
      </form>
    </Card>
  )
}
